import React, { useState, useEffect, useCallback, Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import CategoryRail from './CategoryRail';
import FeaturedHeroCarousel from './FeaturedHeroCarousel';
import LoadingSpinner from './common/LoadingSpinner';
import { HomepageRailsSkeleton } from './common/SkeletonLoaders';
import ErrorBoundary from './common/ErrorBoundary';
import PDPModal from './PDPModal';
import DynamicMetaTags from './SEO/DynamicMetaTags';
import LoginCTASection from './PlaceholderRails/LoginCTASection';
import { useAppLoad } from '../contexts/AppLoadContext';
import { useNavigation } from '../hooks/useNavigation';
import { useContinueWatching } from '../hooks/useContinueWatching';
import { useWatchlist } from '../hooks/useWatchlist';
import { useStreamAuth } from '../hooks/useStreamAuth';
import { userDataService } from '../services/UserDataService';
import './Homepage.css';

const parseTruthyFlag = value => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return ['true', '1', 'yes', 'y'].includes(normalized);
  }
  return false;
};

const hasUpcomingEventsFlag = category => {
  const fields = category?.custom_fields;
  if (!fields) return false;

  if (!Array.isArray(fields) && typeof fields === 'object') {
    return parseTruthyFlag(fields.is_upcoming_events);
  }

  if (!Array.isArray(fields)) return false;

  const field = fields.find(item => {
    const key =
      item?.field_title || item?.name || item?.key || item?.slug || item?.field_name || item?.title;
    return typeof key === 'string' && key.trim().toLowerCase() === 'is_upcoming_events';
  });

  if (!field) return false;
  return parseTruthyFlag(field.field_value ?? field.value ?? field.val ?? field.data);
};

const CategoriesError = ({ error, retry }) => (
  <div className="flex flex-col items-center justify-center min-h-screen p-6">
    <div className="max-w-md text-center">
      <h2 className="mb-4 text-2xl font-bold text-white">Unable to Load Content</h2>
      <p className="mb-6 text-white/70">
        We&apos;re having trouble loading the streaming content. Please check your connection and
        try again.
      </p>
      <button
        onClick={retry}
        className="px-6 py-3 font-medium text-white transition-colors bg-blue-600 rounded-lg hover:bg-blue-700"
      >
        Try Again
      </button>
    </div>
  </div>
);

const Homepage = () => {
  const { categories, loading, error, retry } = useAppLoad();
  const { handleContentSelect, handleCategorySelect } = useNavigation();
  const { continueWatchingData, hasContinueWatching } = useContinueWatching();
  const { watchlistData, hasWatchlist } = useWatchlist();
  const { isAuthenticated } = useStreamAuth();
  const location = useLocation();

  // Watch Again data
  const [watchAgainChannels, setWatchAgainChannels] = useState([]);

  // Resumption data for ThumbnailHoverModal
  const [resumptionData, setResumptionData] = useState({
    continueWatching: [],
    watchAgain: [],
  });

  useEffect(() => {
    const fetchResumptionData = async () => {
      if (!isAuthenticated) {
        setResumptionData({ continueWatching: [], watchAgain: [] });
        return;
      }

      try {
        const data = await userDataService.refreshResumptionsData();
        setResumptionData({
          continueWatching: data?.['continue-watching'] || [],
          watchAgain: data?.['watch-again'] || [],
        });
      } catch (err) {
        console.error('Homepage: Error fetching resumption data:', err);
        setResumptionData({ continueWatching: [], watchAgain: [] });
      }
    };

    fetchResumptionData();
  }, [isAuthenticated]);

  useEffect(() => {
    const fetchWatchAgainData = async () => {
      if (!isAuthenticated) {
        setWatchAgainChannels([]);
        return;
      }
      try {
        // The bridge puts each title in exactly one rail: watch-again is saved
        // >= 95% of a known duration, so a finished title is here and not in
        // Continue Watching. Most recently finished first.
        const items = [...((await userDataService.getWatchAgainData()) || [])].sort(
          (a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0),
        );
        const transformed = items.map(item => ({
          _id: item.parent_channel?._id || item.channel?._id || item._id,
          title: item.parent_channel?.title || item.title,
          slug: item.parent_channel?.slug || item.channel?.slug || item.slug,
          spotlight_poster: item.parent_channel?.spotlight_poster || item.channel?.spotlight_poster,
          poster: item.parent_channel?.poster || item.channel?.poster,
          thumb: item.thumb,
          programming_type:
            item.parent_channel?.programming_type || item.channel?.programming_type || 'Movie',
          channel_type: item.parent_channel ? 'series' : 'video',
        }));
        const seen = new Set();
        const unique = transformed.filter(ch => {
          if (seen.has(ch._id)) return false;
          seen.add(ch._id);
          return true;
        });
        setWatchAgainChannels(unique);
      } catch (err) {
        console.error('Homepage: Error fetching watch again data:', err);
        setWatchAgainChannels([]);
      }
    };
    fetchWatchAgainData();
  }, [isAuthenticated]);

  // PDP Modal state
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [showPDPModal, setShowPDPModal] = useState(false);
  const [hasProcessedPermalink, setHasProcessedPermalink] = useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const channelSlug = searchParams.get('channel');

    if (channelSlug && categories.length > 0 && !hasProcessedPermalink) {
      let foundChannel = null;

      for (const category of categories) {
        if (category.channels && Array.isArray(category.channels)) {
          foundChannel = category.channels.find(
            channel =>
              channel.slug === channelSlug ||
              channel._id === channelSlug ||
              channel.permalink === channelSlug,
          );
          if (foundChannel) {
            break;
          }
        }
      }

      if (foundChannel) {
        setSelectedChannel(foundChannel);
        setShowPDPModal(true);
        setHasProcessedPermalink(true);
      } else {
        setHasProcessedPermalink(true);
      }
    }
  }, [location.search, categories, hasProcessedPermalink]);

  const handlePDPModalClose = () => {
    setShowPDPModal(false);
    setSelectedChannel(null);

    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has('channel')) {
      searchParams.delete('channel');
      const newUrl = `${window.location.pathname}${
        searchParams.toString() ? '?' + searchParams.toString() : ''
      }`;
      window.history.replaceState({}, '', newUrl);
    }
  };

  const onMovieSelect = useCallback(
    movieId => handleContentSelect(movieId, 'movie'),
    [handleContentSelect],
  );

  const onSeriesSelect = useCallback(
    seriesId => handleContentSelect(seriesId, 'series'),
    [handleContentSelect],
  );

  const onCategoryClick = useCallback(
    categoryId => handleCategorySelect(categoryId),
    [handleCategorySelect],
  );

  // Custom fields logic: find the hero category by is_upcoming_events flag
  const heroCategoryIndex = categories.findIndex(hasUpcomingEventsFlag);
  const resolvedHeroIndex = heroCategoryIndex >= 0 ? heroCategoryIndex : 0;
  const featuredCategory = categories[resolvedHeroIndex] || null;
  const regularCategories = categories.filter((_, idx) => idx !== resolvedHeroIndex);
  const getRailWrapperClass = idx =>
    idx === 0 ? 'homepage-first-rail -mt-72 md:-mt-80 xl:-mt-80 z-20 relative' : 'mb-8';

  // Check error before the loading/empty-content guard: a failed appload leaves
  // categories empty, so the guard below would otherwise mask the failure as an
  // infinite spinner and never reach the error + retry UI. fetchAppLoad clears
  // error at the start of every attempt, so a retry shows the spinner, not a
  // stale error.
  if (error) {
    return <CategoriesError error={error} retry={retry} />;
  }

  if (loading || (!featuredCategory && !categories?.length)) {
    return <LoadingSpinner message="Loading streaming content..." />;
  }

  return (
    <div className="min-h-screen bg-primary homepage" style={{ marginTop: '-72px' }}>
      <DynamicMetaTags />

      {/* Full-screen Hero Carousel - pulls up behind transparent header */}
      <ErrorBoundary fallback="Failed to load featured content">
        <Suspense fallback={<LoadingSpinner message="Loading featured content..." />}>
          {featuredCategory && (
            <FeaturedHeroCarousel
              category={featuredCategory}
              onMovieSelect={onMovieSelect}
              onSeriesSelect={onSeriesSelect}
            />
          )}
        </Suspense>
      </ErrorBoundary>

      {/* Content Rails - With overlapping effect */}
      <main className="container relative mx-auto pb-16">
        <ErrorBoundary fallback="Failed to load categories">
          <Suspense fallback={<HomepageRailsSkeleton railCount={3} />}>
            {regularCategories.length > 0 ? (
              regularCategories.map((category, idx) => {
                // Insert Continue Watching / Watchlist / Watch Again at idx 2
                if (idx === 2) {
                  return (
                    <React.Fragment key={`cw-block-${category._id}`}>
                      {/* Continue Watching Rail */}
                      {isAuthenticated && hasContinueWatching && (
                        <div className="mb-8">
                          <CategoryRail
                            category={{
                              _id: 'continue-watching',
                              name: 'Continue Watching',
                              display_name: 'Continue Watching',
                              channels: continueWatchingData,
                              style: 'continue-watching',
                            }}
                            preCalculatedContinueWatching={resumptionData.continueWatching}
                            preCalculatedWatchAgain={resumptionData.watchAgain}
                            onCategorySelect={onCategoryClick}
                            onMovieSelect={onMovieSelect}
                            onSeriesSelect={onSeriesSelect}
                          />
                        </div>
                      )}

                      {/* Watchlist Rail */}
                      {isAuthenticated && hasWatchlist && (
                        <div className="mb-8">
                          <CategoryRail
                            category={{
                              _id: 'my-watchlist',
                              name: 'My List',
                              display_name: 'My List',
                              channels: watchlistData,
                            }}
                            preCalculatedContinueWatching={resumptionData.continueWatching}
                            preCalculatedWatchAgain={resumptionData.watchAgain}
                            onCategorySelect={onCategoryClick}
                            onMovieSelect={onMovieSelect}
                            onSeriesSelect={onSeriesSelect}
                          />
                        </div>
                      )}

                      {/* Watch Again Rail */}
                      {isAuthenticated && watchAgainChannels.length > 0 && (
                        <div className="mb-8">
                          <CategoryRail
                            category={{
                              _id: 'watch-again',
                              name: 'Watch Again',
                              display_name: 'Watch Again',
                              channels: watchAgainChannels,
                            }}
                            preCalculatedContinueWatching={resumptionData.continueWatching}
                            preCalculatedWatchAgain={resumptionData.watchAgain}
                            onCategorySelect={onCategoryClick}
                            onMovieSelect={onMovieSelect}
                            onSeriesSelect={onSeriesSelect}
                          />
                        </div>
                      )}

                      {/* Continue Watching CTA for Non-Authenticated Users */}
                      {!isAuthenticated && (
                        <LoginCTASection
                          sectionTitle="Continue Watching"
                          title="Pick up right where you left off."
                          subtitle="Login now to continue watching your favorite series & movies."
                        />
                      )}

                      {/* Regular Category Rail */}
                      <div className={getRailWrapperClass(idx)}>
                        <CategoryRail
                          category={category}
                          preCalculatedContinueWatching={resumptionData.continueWatching}
                          preCalculatedWatchAgain={resumptionData.watchAgain}
                          forceTextTitle={idx === 0}
                          onCategorySelect={onCategoryClick}
                          onMovieSelect={onMovieSelect}
                          onSeriesSelect={onSeriesSelect}
                        />
                      </div>
                    </React.Fragment>
                  );
                }

                // Insert Watch Again CTA for non-authenticated users at index 4
                if (idx === 4 && !isAuthenticated) {
                  return (
                    <React.Fragment key={`wa-cta-${category._id}`}>
                      <LoginCTASection
                        sectionTitle="Watch Again"
                        title="Rediscover your favorites."
                        subtitle="Login now to watch again your favorite series & movies."
                      />
                      <div className="mb-8">
                        <CategoryRail
                          category={category}
                          preCalculatedContinueWatching={resumptionData.continueWatching}
                          preCalculatedWatchAgain={resumptionData.watchAgain}
                          onCategorySelect={onCategoryClick}
                          onMovieSelect={onMovieSelect}
                          onSeriesSelect={onSeriesSelect}
                        />
                      </div>
                    </React.Fragment>
                  );
                }

                // Regular category rail
                return (
                  <div key={category._id} className={getRailWrapperClass(idx)}>
                    <CategoryRail
                      category={category}
                      preCalculatedContinueWatching={resumptionData.continueWatching}
                      preCalculatedWatchAgain={resumptionData.watchAgain}
                      forceTextTitle={idx === 0}
                      onCategorySelect={onCategoryClick}
                      onMovieSelect={onMovieSelect}
                      onSeriesSelect={onSeriesSelect}
                    />
                  </div>
                );
              })
            ) : (
              <HomepageRailsSkeleton railCount={5} />
            )}
          </Suspense>
        </ErrorBoundary>
      </main>

      {/* PDP Modal for direct channel links */}
      {selectedChannel && (
        <PDPModal
          isVisible={showPDPModal}
          channel={selectedChannel}
          onClose={handlePDPModalClose}
        />
      )}
    </div>
  );
};

export default Homepage;
